import { apiRequest } from '@/lib/api-client';
import { getStoredAuthToken } from '@/lib/auth-storage';

export type CategoriaApi = {
  id: number;
  nombre: string;
  descripcion?: string;
  orden?: number;
  activa?: boolean;
  created_at?: string;
  updated_at?: string;
};

export type CategoriaTematicaApi = {
  id: number;
  categoria_id?: number;
  nombre: string;
  descripcion?: string;
  orden?: number;
  activa?: boolean;
  categoria?: {
    id: number;
    nombre: string;
  } | null;
};

export type TipoIndicadorApi = {
  id: number;
  nombre: string;
  codigo?: string;
  descripcion?: string;
  activo?: boolean;
};

const normalizeList = <T,>(payload: unknown): T[] => {
  if (Array.isArray(payload)) {
    return payload as T[];
  }

  if (payload && typeof payload === 'object') {
    const data = (payload as { data?: unknown[] }).data;
    if (Array.isArray(data)) {
      return data as T[];
    }
  }

  return [];
};

export const fetchCategorias = async (options: { onlyActive?: boolean } = {}): Promise<CategoriaApi[]> => {
  const token = getStoredAuthToken();
  const activeFilter = options.onlyActive === false ? '' : '&activa=1';
  const payload = await apiRequest<unknown>(`/api/catalogos/categorias?per_page=100${activeFilter}`, { token });
  return normalizeList<CategoriaApi>(payload);
};

export const fetchCategoriasTematicas = async (options: { onlyActive?: boolean } = {}): Promise<CategoriaTematicaApi[]> => {
  const token = getStoredAuthToken();
  const activeFilter = options.onlyActive === false ? '' : '&activa=1';
  const payload = await apiRequest<unknown>(`/api/catalogos/categorias-tematicas?per_page=100${activeFilter}`, { token });
  return normalizeList<CategoriaTematicaApi>(payload);
};

export const fetchCategoriaTematica = async (id: number | string): Promise<CategoriaTematicaApi> => {
  const token = getStoredAuthToken();
  return apiRequest<CategoriaTematicaApi>(`/api/catalogos/categorias-tematicas/${id}`, { token });
};

export const createCategoriaTematica = async (
  data: Partial<CategoriaTematicaApi>,
): Promise<CategoriaTematicaApi> => {
  const token = getStoredAuthToken();
  return apiRequest<CategoriaTematicaApi>('/api/catalogos/categorias-tematicas', {
    token,
    method: 'POST',
    body: JSON.stringify(data),
  });
};

export const updateCategoriaTematica = async (
  id: number | string,
  data: Partial<CategoriaTematicaApi>,
): Promise<CategoriaTematicaApi> => {
  const token = getStoredAuthToken();
  return apiRequest<CategoriaTematicaApi>(`/api/catalogos/categorias-tematicas/${id}`, {
    token,
    method: 'PUT',
    body: JSON.stringify(data),
  });
};

export const fetchTiposIndicador = async (): Promise<TipoIndicadorApi[]> => {
  const token = getStoredAuthToken();
  const payload = await apiRequest<unknown>('/api/catalogos/tipos-indicador?per_page=100&activo=1', { token });
  return normalizeList<TipoIndicadorApi>(payload);
};

export const createCategoria = async (data: Partial<CategoriaApi>): Promise<CategoriaApi> => {
  const token = getStoredAuthToken();
  return apiRequest<CategoriaApi>('/api/catalogos/categorias', {
    token,
    method: 'POST',
    body: JSON.stringify(data),
  });
};

export const fetchCategoria = async (id: number | string): Promise<CategoriaApi> => {
  const token = getStoredAuthToken();
  return apiRequest<CategoriaApi>(`/api/catalogos/categorias/${id}`, { token });
};

export const updateCategoria = async (
  id: number | string,
  data: Partial<CategoriaApi>,
): Promise<CategoriaApi> => {
  const token = getStoredAuthToken();
  return apiRequest<CategoriaApi>(`/api/catalogos/categorias/${id}`, {
    token,
    method: 'PUT',
    body: JSON.stringify(data),
  });
};

export type UnidadMedidaApi = {
  id: number;
  nombre: string;
  simbolo?: string;
  descripcion?: string;
  activa?: boolean;
  created_at?: string;
  updated_at?: string;
};

export const fetchUnidades = async (options: { onlyActive?: boolean } = {}): Promise<UnidadMedidaApi[]> => {
  const token = getStoredAuthToken();
  const activeFilter = options.onlyActive === false ? '' : (options.onlyActive === true ? '&activa=1' : '');
  const payload = await apiRequest<unknown>(`/api/catalogos/unidades-medida?per_page=100${activeFilter}`, { token });
  return normalizeList<UnidadMedidaApi>(payload);
};

export const fetchUnidad = async (id: number | string): Promise<UnidadMedidaApi> => {
  const token = getStoredAuthToken();
  return apiRequest<UnidadMedidaApi>(`/api/catalogos/unidades-medida/${id}`, { token });
};

export const createUnidad = async (data: Partial<UnidadMedidaApi>): Promise<UnidadMedidaApi> => {
  const token = getStoredAuthToken();
  return apiRequest<UnidadMedidaApi>('/api/catalogos/unidades-medida', {
    token,
    method: 'POST',
    body: JSON.stringify(data),
  });
};

export const updateUnidad = async (
  id: number | string,
  data: Partial<UnidadMedidaApi>,
): Promise<UnidadMedidaApi> => {
  const token = getStoredAuthToken();
  return apiRequest<UnidadMedidaApi>(`/api/catalogos/unidades-medida/${id}`, {
    token,
    method: 'PUT',
    body: JSON.stringify(data),
  });
};

export const deleteUnidad = async (id: number | string): Promise<void> => {
  const token = getStoredAuthToken();
  return apiRequest<void>(`/api/catalogos/unidades-medida/${id}`, {
    token,
    method: 'DELETE',
  });
};

export type PeriodicidadApi = {
  id: number;
  nombre: string;
  dias?: number;
};

export type ModalidadCargaApi = {
  id: number;
  codigo?: string;
  nombre: string;
  descripcion?: string;
};

export const fetchPeriodicidades = async (): Promise<PeriodicidadApi[]> => {
  const token = getStoredAuthToken();
  const payload = await apiRequest<unknown>('/api/catalogos/periodicidades?per_page=100', { token });
  return normalizeList<PeriodicidadApi>(payload);
};

export type EstadoDatoApi = {
  id: number;
  codigo?: string;
  nombre: string;
  descripcion?: string;
};

export const fetchEstadosDato = async (): Promise<EstadoDatoApi[]> => {
  const token = getStoredAuthToken();
  const payload = await apiRequest<unknown>('/api/catalogos/estados-dato?per_page=100', { token });
  return normalizeList<EstadoDatoApi>(payload);
};

export const fetchModalidadesCarga = async (): Promise<ModalidadCargaApi[]> => {
  const token = getStoredAuthToken();
  const payload = await apiRequest<unknown>('/api/catalogos/modalidades-carga?per_page=100', { token });
  return normalizeList<ModalidadCargaApi>(payload);
};

export const createPeriodicidad = async (data: Partial<PeriodicidadApi>): Promise<PeriodicidadApi> => {
  const token = getStoredAuthToken();
  return apiRequest<PeriodicidadApi>('/api/catalogos/periodicidades', {
    token,
    method: 'POST',
    body: JSON.stringify(data),
  });
};

export type TemaApi = {
  id: number;
  nombre: string;
  numero?: number | string;
  descripcion?: string;
  orden?: number;
  activo?: boolean;
};

export const fetchTemas = async (options: { onlyActive?: boolean } = {}): Promise<TemaApi[]> => {
  const token = getStoredAuthToken();
  const activeFilter = options.onlyActive === false ? '' : '&activo=1';
  try {
    const payload = await apiRequest<unknown>(`/api/catalogos/temas?per_page=100${activeFilter}`, { token });
    const list = normalizeList<TemaApi>(payload);
    if (list.length > 0) return list;
  } catch (err) {
    console.warn('Fallback a /api/catalogos/categorias al obtener temas:', err);
  }
  // Fallback to categorias if temas endpoint is not yet migrated in old environments
  const fallback = await fetchCategorias(options);
  return fallback.map((c) => ({
    id: c.id,
    nombre: c.nombre,
    descripcion: c.descripcion,
    orden: c.orden,
    activo: c.activa,
  }));
};

export type OdsApi = {
  id: number;
  numero?: number | string;
  codigo?: string;
  nombre: string;
  descripcion?: string;
  icono_url?: string | null;
  color?: string | null;
};

export const fetchOds = async (): Promise<OdsApi[]> => {
  const token = getStoredAuthToken();
  try {
    const payload = await apiRequest<unknown>('/api/catalogos/ods?per_page=100', { token });
    return normalizeList<OdsApi>(payload);
  } catch (err) {
    console.warn('Failed to load ODS catalog:', err);
    return [];
  }
};

